// KYCKCONNECT SERVICES (redesign Phase 1, master plan principle 4). PURE.
//
// ONE definition of which KyckConnect services a club uses, from data that already exists. Everything that adapts to services
// (the account tab's badges, the Overview's filters, and later the portal dashboard) reads this, so they cannot disagree.
//
//   leadRelay   - at least one lead route points at the club (active or not: the route exists, so the relay is set up for it).
//   aiSms       - the club has AI SMS switched on AND an active bot to answer. A bot with the switch off, or a switch with no
//                 active bot, is not a running service (the setup step says which half is missing).
//   speedToLead - calls on, or inbound call forwarding on. The staff list is a SETUP requirement of the service, not part of
//                 whether it is on, so an empty staff list reads as "on, incomplete" rather than "off".

export interface KyckConnectServiceFacts {
  routeCount: number;
  samEnabled: boolean;
  /** The club's active bot (the settings' chosen bot, else the active one); null when there is none. */
  activeBot: { name: string | null } | null;
  speedToLeadEnabled: boolean;
  inboundEnabled: boolean;
}

export interface KyckConnectServices {
  leadRelay: boolean;
  aiSms: { on: boolean; botName: string | null };
  speedToLead: boolean;
}

export function deriveKyckConnectServices(f: KyckConnectServiceFacts): KyckConnectServices {
  const aiOn = f.samEnabled && f.activeBot !== null;
  return {
    leadRelay: f.routeCount > 0,
    aiSms: { on: aiOn, botName: aiOn ? (f.activeBot?.name?.trim() || null) : null },
    speedToLead: f.speedToLeadEnabled || f.inboundEnabled,
  };
}

export const hasAnyKyckConnectService = (s: KyckConnectServices): boolean => s.leadRelay || s.aiSms.on || s.speedToLead;

export type ServiceBadgeId = 'leadRelay' | 'aiSms' | 'speedToLead';

/** The badges the tab header and the Overview show. AI SMS carries the bot's name when it has one. */
export function serviceBadges(s: KyckConnectServices): Array<{ id: ServiceBadgeId; label: string }> {
  const out: Array<{ id: ServiceBadgeId; label: string }> = [];
  if (s.leadRelay) out.push({ id: 'leadRelay', label: 'Lead relay' });
  if (s.aiSms.on) out.push({ id: 'aiSms', label: s.aiSms.botName ? `AI SMS (${s.aiSms.botName})` : 'AI SMS' });
  if (s.speedToLead) out.push({ id: 'speedToLead', label: 'Speed to Lead' });
  return out;
}
